/**
 * FocusGroupSession — the live focus-group discussion surface (card or modal body).
 *
 * PRESENTATIONAL by design: it receives the room view-model (status / participants /
 * messages / thinking) from the page-level `useChatRoom(activeFocusToken)` instance
 * and renders the live discussion with the SAME `ChatThread` the standalone
 * `/chats/:token` page uses — no forked engine, pure composition.
 *
 * Lives under ChatPage so it can compose ChatThread / ParticipantPopover without
 * a components→pages import inversion. Hosts that omit `onSend` suppress the
 * in-card composer and drive sends from their own page composer.
 */
import { Maximize2, MessagesSquare, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type {
  ChatRoomMessageDto,
  ChatRoomParticipantDto,
  ChatRoomReactionDto,
  ChatRoomStatus,
} from '@digital-twins/shared-types'
import { isIntroductionMessage } from '@digital-twins/shared-types'
import { ChatThread } from '@/pages/ChatPage/ChatThread/ChatThread'
import type { QueueBlockedReason, QueuedMessage } from '@/pages/ChatPage/useChatRoom'
import type { ChatImage } from '@/lib/api'
import { ParticipantPopover } from '@/pages/ChatPage/ChatSidebar/ParticipantPopover'
import { usePersonaThumbFallback } from '@/components/shared/PersonaThumbImg'
import { cn } from '@/lib/utils'
import { TwinsMarkLoader } from '@/components/quick-study-results/TwinsMarkLoader'

import type { AudienceTag } from '@digital-twins/shared-types'

const FALLBACK_IMAGE = '/images/twin-fallback.png'

/**
 * ParticipantAvatarStack — who you're talking to, at a glance. Mirrors the overlapping
 * avatar-circle treatment from `ConversationCard`'s `AvatarStack` (Conversations lobby)
 * so a hosted focus group reads consistently with the rest of the app instead of a
 * generic chat-bubble icon. Each avatar is wrapped in the SAME `ParticipantPopover` the
 * chat sidebar uses, so hovering surfaces the twin's real profile (photo, blurb, bio) —
 * not just a face with no data behind it. Falls back to the icon badge only when there
 * are no participants yet (e.g. the room is still preparing).
 */
export function ParticipantAvatarStack({
  participants,
  populationId,
  segmentTags,
}: {
  participants: Array<ChatRoomParticipantDto>
  populationId?: string | null
  /** Session subsegment tags, surfaced inside each twin's profile popover (every seated
   *  twin belongs to the segment, so it lives on the twin, not a separate header row). */
  segmentTags?: Array<AudienceTag>
}) {
  if (participants.length === 0) {
    return (
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-twin-chat-bubble/20">
        <MessagesSquare className="h-4 w-4 text-twin-chat-fg" />
      </span>
    )
  }

  const displayed = participants.slice(0, 4)
  const overflow = participants.length > 4 ? participants.length - 3 : 0
  const slots = overflow > 0 ? displayed.slice(0, 3) : displayed

  const avatarFor = (p: ChatRoomParticipantDto) => (
    <FocusGroupHeaderAvatar key={p.id} participant={p} />
  )

  return (
    <div className="flex shrink-0 -space-x-2" data-testid="qs-focus-group-avatars">
      {slots.map((p) =>
        populationId ? (
          <ParticipantPopover key={p.id} participant={p} populationId={populationId} segmentTags={segmentTags}>
            {avatarFor(p)}
          </ParticipantPopover>
        ) : (
          <span key={p.id}>{avatarFor(p)}</span>
        ),
      )}
      {overflow > 0 && (
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-twin-chat-bubble/40 text-[10px] font-medium text-twin-chat-fg ring-2 ring-twin-chat-bg">
          +{overflow}
        </span>
      )}
    </div>
  )
}

// Pulled into its own component (rather than inline in `avatarFor`) so each
// tile can hold its own thumbnail/original fallback state.
function FocusGroupHeaderAvatar(props: { participant: ChatRoomParticipantDto }) {
  const { participant } = props
  const { displayUrl, handleImageError } = usePersonaThumbFallback(participant.imageUrl, 64)

  return (
    <img
      src={displayUrl || FALLBACK_IMAGE}
      alt={participant.displayName}
      className="h-8 w-8 shrink-0 rounded-full object-cover ring-2 ring-twin-chat-bg"
      onError={(e) => {
        const target = e.target as HTMLImageElement
        handleImageError(() => {
          target.src = FALLBACK_IMAGE
        })
      }}
    />
  )
}

interface FocusGroupSessionProps {
  /** Header label, e.g. "Focus group · <insight>", or the twin's name for a 1:1 chat. */
  title: string
  /** True for a 1:1 chat with a single twin — the session reads as a conversation with that
   *  twin (no "focus group" wording in the subtitle or controls). */
  solo?: boolean
  participants: Array<ChatRoomParticipantDto>
  /** The audience the seated twins belong to — enables the header avatar popovers
   *  (ParticipantPopover fetches the twin's full profile by population + persona id).
   *  Omitted → avatars still render, just without the hover profile. */
  populationId?: string | null
  roomStatus: ChatRoomStatus
  messages: Array<ChatRoomMessageDto>
  thinkingParticipantIds?: Array<string>
  isSending?: boolean
  isWaitingForTurn?: boolean
  queueBlockedReason?: QueueBlockedReason | null
  /** End the focus group → closes the room, writes the summary, returns to Explore mode. */
  onEnd: () => void
  /** Optional: open the roomier full-page chat view via ROUTES.chatRoom. */
  onExpand?: () => void
  messagesError?: boolean
  onRetryMessages?: () => void
  /**
   * When true, introduction messages (the per-twin welcome turns) are hidden.
   * Use in inline focus groups where introductions add noise.
   */
  hideIntroductions?: boolean
  /**
   * When true, the component fills its flex container (flex-1 min-h-0) rather than
   * capping at max-h-[520px]. Use when the host renders it as a full-height pane.
   */
  fullHeight?: boolean
  /** Replaces the default "start the conversation" copy shown in an empty room. */
  emptyStateText?: string
  /** Headline over the roster in an empty room (see ChatThread). Only takes effect with onSend. */
  emptyHeadline?: string
  /** Segment-aware: the session's chosen subsegment tags, shown as chips under the header.
   *  Empty / absent (OFF or no tags) hides the chips so the card is byte-identical to today. */
  segmentTags?: Array<AudienceTag>
  className?: string
  /**
   * Provide a send handler to render the real composer inside the card. Hosts that
   * already have an outer composer omit it; the guest focus group (no outer composer)
   * passes it through.
   */
  onSend?: (
    content: string,
    images?: Array<ChatImage>,
    captureUrl?: string,
    stimulusVideos?: Array<{ url: string; label?: string; mimeType?: string }>,
  ) => void
  messageQueue?: Array<QueuedMessage>
  /** C13: the room's resolved reactions, passed through to the thread. */
  reactions?: Array<ChatRoomReactionDto>
  liveReactionKeys?: ReadonlySet<string>
  onQueueEdit?: (id: string, content: string) => void
  onQueueRemove?: (id: string) => void
  onQueueRetry?: (id: string) => void
  blockedQueuedId?: string | null
  sendError?: string | null
  composerPlaceholder?: string
  /** Room the messages belong to — needed for image keys outside the standalone chat route. */
  roomToken?: string
  /** Resolves stored image keys to URLs (guests go through the share route). */
  getImageUrl?: (token: string, key: string) => string
}

export function FocusGroupSession({
  title,
  solo = false,
  participants,
  populationId,
  roomStatus,
  messages,
  thinkingParticipantIds = [],
  isSending = false,
  isWaitingForTurn = false,
  queueBlockedReason = null,
  onEnd,
  onExpand,
  messagesError = false,
  onRetryMessages,
  hideIntroductions = false,
  fullHeight = false,
  emptyStateText,
  emptyHeadline,
  segmentTags,
  className,
  onSend,
  messageQueue,
  onQueueEdit,
  onQueueRemove,
  onQueueRetry,
  blockedQueuedId,
  sendError,
  composerPlaceholder,
  roomToken,
  getImageUrl,
  reactions,
  liveReactionKeys,
}: FocusGroupSessionProps) {
  const { t } = useTranslation('app')
  const preparing = roomStatus === 'preparing'

  const displayMessages = hideIntroductions
    ? messages.filter((m) => !isIntroductionMessage(m.metadataType, m.metadata))
    : messages

  // When introductions are hidden, also suppress the per-twin thinking dots that appear
  // while they compose their intro turns — those dots would flash meaninglessly since the
  // intro messages themselves are hidden. Only show thinking once the opener (a user
  // message) has arrived, signalling the real discussion has started.
  const openerSent = hideIntroductions
    ? messages.some((m) => m.senderType === 'user')
    : true
  const displayThinkingIds = openerSent ? thinkingParticipantIds : []

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-xl border border-twin-chat-divider bg-twin-chat-surface',
        fullHeight ? 'min-h-0 flex-1' : 'max-h-[520px] min-h-[280px]',
        className,
      )}
      data-testid="qs-focus-group-session"
      data-room-status={roomStatus}
    >
      {/* Header — mirrors the standalone chat page header: generous padding, a
          translucent divider (not a solid rule), and text-driven pill buttons rather
          than a dense bordered bar, so a hosted focus group reads like the rest of the
          app's chat surfaces instead of a squeezed-in card header. */}
      <div className="flex shrink-0 items-center gap-2.5 border-b border-twin-chat-divider/20 px-4 py-3.5 sm:px-6">
        <ParticipantAvatarStack participants={participants} populationId={populationId} segmentTags={segmentTags} />
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span
            className="min-w-0 truncate text-sm font-medium text-twin-chat-fg"
            data-testid="qs-focus-group-title"
          >
            {title}
          </span>
          <span className="shrink-0 text-xs text-twin-chat-fg-muted">
            {solo
              ? t('quickStudyChrome.twinCount', { count: 1 })
              : t('quickStudyChrome.twinCount', { count: participants.length })}
          </span>
        </div>
        {onExpand ? (
          <button
            type="button"
            onClick={onExpand}
            aria-label={solo ? t('quickStudyChrome.openFullConversation') : t('quickStudyChrome.openFullFocusGroup')}
            title={solo ? t('quickStudyChrome.openFullConversationView') : t('quickStudyChrome.openFullFocusGroupView')}
            className="shrink-0 rounded-md p-1.5 text-twin-chat-fg-muted transition-colors hover:bg-twin-chat-bubble/30 hover:text-twin-chat-fg"
            data-testid="qs-focus-group-expand"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </button>
        ) : null}
        <button
          type="button"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onEnd()
          }}
          aria-label={solo ? t('quickStudyChrome.endConversation') : t('quickStudyChrome.endFocusGroup')}
          className="flex shrink-0 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-twin-chat-fg-muted transition-colors hover:bg-twin-chat-bubble/30 hover:text-twin-chat-fg"
          data-testid="qs-focus-group-end"
        >
          <X className="h-3.5 w-3.5" />
          {solo ? t('quickStudyChrome.endChat') : t('quickStudyChrome.endFocusGroup')}
        </button>
      </div>

      {/* Subsegment cuts are no longer shown as a header row: every seated twin belongs to
          the chosen subsegment, so the tag surfaces inside each twin's profile popover
          (see ParticipantAvatarStack → ParticipantPopover segmentTags) instead. */}

      {/* Body — a compact inline preparing spinner (PreparingRoom is full-page, unsuited
          to a card), else the reused ChatThread. The composer is suppressed when the room
          is active (the page composer drives sends); when closed, ChatThread shows its own
          ClosedRoomBanner (we pass no override so that read-only banner renders). */}
      {preparing ? (
        <div
          className="flex flex-1 flex-col items-center justify-center gap-2 py-10 text-center"
          data-testid="qs-focus-group-preparing"
        >
          <TwinsMarkLoader className="h-6 w-6 text-twin-chat-send" />
          <p className="text-sm text-twin-chat-fg-muted">{t('quickStudyChrome.preparingRoom')}</p>
        </div>
      ) : (
        <ChatThread
          messages={displayMessages}
          participants={participants}
          roomStatus={roomStatus}
          onSend={onSend ?? (() => {})}
          thinkingParticipantIds={displayThinkingIds}
          isSending={isSending}
          isWaitingForTurn={isWaitingForTurn}
          queueBlockedReason={queueBlockedReason}
          // When introductions are hidden, never trigger MessageList's "fill the empty
          // room with everyone-is-typing" fallback before the opener — those per-twin
          // dots used to precede the (now removed) greeting turns, so here they'd just
          // be meaningless loading noise.
          isRoomActive={roomStatus === 'active' && openerSent}
          composerOverride={
            onSend || roomStatus === 'closed' ? undefined : <></>
          }
          messageQueue={messageQueue}
          onQueueEdit={onQueueEdit}
          onQueueRemove={onQueueRemove}
          onQueueRetry={onQueueRetry}
          blockedQueuedId={blockedQueuedId}
          sendError={sendError}
          placeholder={composerPlaceholder}
          emptyStateText={emptyStateText}
          emptyHeadline={emptyHeadline}
          populationId={populationId}
          messagesError={messagesError}
          onRetryMessages={onRetryMessages}
          roomToken={roomToken}
          getImageUrl={getImageUrl}
          reactions={reactions}
          liveReactionKeys={liveReactionKeys}
        />
      )}
    </div>
  )
}
